"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.TaskService = void 0;
const types_1 = require("../types");
const errors_1 = require("../errors");
const parsePrompt_1 = __importDefault(require("../utils/parsePrompt"));
class TaskService {
    constructor(taskRegistry, agentService) {
        this.taskRegistry = taskRegistry;
        this.agentService = agentService;
        this.onNewTask = () => { };
        this.processedTasks = new Map();
    }
    /**
     * Creates a new task.
     * @param {TaskCreationParams} params - The parameters for task creation.
     * @returns {Promise<TaskData>} A promise that resolves to the task ID.
     */
    async createTask(params) {
        try {
            const proposal = await this.agentService.getProposal(params.proposalId);
            const tx = await this.taskRegistry.createTask(params.prompt, params.proposalId, { value: proposal.price });
            const receipt = await tx.wait();
            const event = this.findEventInReceipt(receipt, "TaskCreated");
            // if (!event?.args?.[1]) {
            //   throw new Error("Task creation failed: No task address in event");
            // }
            // const taskId = event.args[1];
            const taskId = event.args[2];
            const prompt = event.args[4];
            return {
                id: taskId,
                assignee: event.args[1],
                prompt: prompt,
                status: BigInt(types_1.TaskStatus.CREATED),
                issuer: event.args[0],
                proposalId: BigInt(params.proposalId),
            };
        }
        catch (error) {
            // console.error("Task creation failed:", error);
            if (error.reason === "Proposal not found") {
                throw new errors_1.ProposalNotFoundError(error.reason);
            }
            throw error;
        }
    }
    /**
     * Gets data for a specific task.
     * @param {string} taskId - The ID of the task.
     * @returns {Promise<TaskData>} A promise that resolves to the task data.
     */
    async getTaskData(taskId) {
        const { id, prompt, issuer, status, assignee, proposalId, rating } = await this.taskRegistry.tasks(taskId);
        return {
            id,
            prompt,
            assignee: assignee || undefined,
            status,
            issuer,
            rating,
            proposalId,
        };
    }
    /**
     * Gets tasks by issuer.
     * @param {string} issuer - The issuer of the tasks.
     * @returns {Promise<bigint[]>} A promise that resolves to the task IDs.
     */
    async getTasksByIssuer(issuer) {
        return this.taskRegistry.getTasksByIssuer(issuer);
    }
    /**
     * Completes a task.
     * @param {BigNumberish} taskId - The ID of the task.
     * @param {string} result - The result of the task.
     * @returns {Promise<void>} A promise that resolves when the task is completed.
     */
    async completeTask(taskId, result) {
        try {
            const tx = await this.taskRegistry.completeTask(taskId, result);
            await tx.wait();
        }
        catch (error) {
            console.error("Completing task failed:", error);
            throw error;
        }
    }
    /**
     * Assigns a rating to a task.
     * @param {string} taskId - The ID of the task.
     * @param {number} rating - The rating.
     * @returns {Promise<void>} A promise that resolves when the task is assigned.
     */
    async rateTask(taskId, rating) {
        try {
            const tx = await this.taskRegistry.rateTask(taskId, rating);
            await tx.wait();
        }
        catch (error) {
            console.error("Rating task failed:", error);
            throw error;
        }
    }
    /**
     * Cancels a task.
     * @param {string} taskId - The ID of the task.
     * @returns {Promise<void>} A promise that resolves when the task is canceled.
     */
    async cancelTask(taskId) {
        try {
            const tx = await this.taskRegistry.cancelTask(taskId);
            await tx.wait();
        }
        catch (error) {
            console.error("Canceling task failed:", error);
            throw error;
        }
    }
    /**
     * Subscribes to new task creation events.
     */
    async subscribe() {
        console.log("Subscribing to TaskCreated events");
        const agentAddress = await this.agentService.getAgentAddress();
        const filter = this.taskRegistry.filters.TaskCreated(undefined, agentAddress);
        this.taskRegistry.on(filter, async ({ args: [issuer, assignee, taskId, proposalId, prompt] }) => {
            if (this.processedTasks.get(taskId)) {
                console.log("Task already processed");
                return;
            }
            console.log(`New Task Created Event => Issuer: ${issuer} - Assignee: ${assignee} - TaskId: ${taskId} - ProposalId: ${proposalId} - Prompt: ${prompt}`);
            const proposal = await this.agentService.getProposal(proposalId.toString());
            const params = (0, parsePrompt_1.default)(prompt);
            // TODO: handle cases of error been thrown by handler
            this.onNewTask({
                id: taskId,
                status: BigInt(types_1.TaskStatus.CREATED),
                serviceName: proposal.serviceName,
                issuer,
                prompt,
                assignee,
                proposalId,
                params,
            });
            this.processedTasks.set(taskId, true);
        });
    }
    /**
     * Unsubscribes from new task creation events.
     */
    unsubscribe() {
        const filter = this.taskRegistry.filters.TaskCreated();
        this.taskRegistry.removeListener(filter, this.onNewTask);
    }
    /**
     * Sets a listener for new task creation events.
     * @param {function} listener - The listener function.
     */
    setOnNewTaskListener(listener) {
        this.onNewTask = listener;
    }
    findEventInReceipt(receipt, eventName) {
        const events = receipt.logs
            .map((log) => {
            try {
                const event = this.taskRegistry.interface.parseLog(log);
                return event;
            }
            catch (e) {
                console.error("error:", e);
                return null;
            }
        })
            .filter((event) => event !== null);
        const event = events?.find((e) => e.name === eventName);
        return event;
    }
}
exports.TaskService = TaskService;
